/**
 * Phase 3 deliverable — workflow canvas demo.
 *
 * Four modes:
 *
 *  1. **Sample** — hardcoded multi-node workflow rendered read-only.
 *     Exercises every palette kind plus a multi-exit branch.
 *  2. **Paste JSON** — DSL JSON in, read-only canvas out. Same shape
 *     the subscriptions designer accepts.
 *  3. **Author** — palette + editable canvas + live ``toDsl`` JSON
 *     preview. Drop palette items to create nodes, drag handles to
 *     connect, Backspace to delete, click to select.
 *  4. **Kit** — the Lara Canvas kit anatomy (PDF page 3) rendered from
 *     the real `canvas-kit/` components.
 *
 * Subscription-fetch + per-kind inspector pane land in 3c/3d once
 * the API client + validation endpoint ship.
 *
 * Trailing-underscore filename keeps the route off the
 * portal/workflows.tsx layout.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ReadOnlyCanvas } from "@/components/portal/workflow-flow-v2/ReadOnlyCanvas";
import { EditableCanvas } from "@/components/portal/workflow-flow-v2/EditableCanvas";
import {
  defaultSummaryFor,
  kindToFlowType,
  nextNodeId,
  pendingConnectionEdge,
  placementForPick,
  type PendingConnection,
} from "@/components/portal/workflow-flow-v2/canvas-utils";
import { Palette } from "@/components/portal/workflow-flow-v2/Palette";
import { Inspector } from "@/components/portal/workflow-flow-v2/Inspector";
import { ValidationPanel } from "@/components/portal/workflow-flow-v2/ValidationPanel";
import { TestRunPanel } from "@/components/portal/workflow-flow-v2/TestRunPanel";
import type {
  DslWorkflow,
  FlowEdge,
  FlowNode,
} from "@/components/portal/workflow-flow-v2/types";
import {
  validateTopology,
  type ValidationIssue,
} from "@/lib/workflow-validation";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import {
  configMapFromWorkflow,
  toDsl,
  toFlowGraph,
  type DslConfigMap,
} from "@/lib/workflow-graph-adapter";
import { layoutFlowGraph } from "@/components/portal/workflow-flow-v2/layout";
import { KitShowcase } from "@/components/portal/canvas-kit/KitShowcase";

export const Route = createFileRoute("/portal/workflows/canvas-demo")({
  component: CanvasDemoPage,
});

const SAMPLE_WORKFLOW: DslWorkflow = {
  workflow_id: "wf_sample_invoice_triage",
  version: 1,
  organisation_id: "00000000-0000-0000-0000-000000000000",
  name: "Sample · invoice triage",
  triggers: [
    {
      id: "invoice_in",
      kind: "trigger.redis_stream",
      entry_node_id: "load_history",
      config: { stream: "events.b2c.invoice" },
    },
  ],
  nodes: [
    {
      id: "load_history",
      kind: "logic.sql_query",
      config: {
        connector_key: "00000000-0000-0000-0000-000000000001",
        direction: "source",
        query: "",
      },
      next: { success: "classify" },
    },
    {
      id: "classify",
      kind: "agent.pydantic_ai",
      config: {
        agent_type: "intelligent_classifier",
        task_type: "content_classification",
        input: { content: "${trigger.payload.LineItems}" },
      },
      next: { success: "branch_amount" },
    },
    {
      id: "branch_amount",
      kind: "logic.branch",
      config: {
        branches: [
          { name: "high", when: "${trigger.payload.Total > 1000}" },
          { name: "low", when: "true" },
        ],
      },
      next: { high: "approve", low: "notify_low" },
    },
    {
      id: "approve",
      kind: "human.approval",
      config: {
        form_id: "00000000-0000-0000-0000-000000000000",
        prompt: "Approve high-value invoice ${trigger.payload.Total}",
        timeout_hours: 48,
        on_timeout: "auto_reject",
      },
      next: {
        approved: "writeback",
        rejected: "notify_rejected",
        auto_reject: "notify_rejected",
      },
    },
    {
      id: "writeback",
      kind: "action.b2c_write",
      config: {
        connector_key: "xero-prod",
        record_type: "Invoice",
        operation: "update",
        target_id: "${trigger.payload.Id}",
        payload: { Status: "AUTHORISED" },
      },
      next: { success: "notify_high" },
    },
    {
      id: "notify_high",
      kind: "action.messaging_send",
      config: {
        conversation_id: "${org.conversations.finance_alerts}",
        content: "High-value invoice ${trigger.payload.Total} authorised.",
      },
    },
    {
      id: "notify_rejected",
      kind: "action.messaging_send",
      config: {
        conversation_id: "${org.conversations.finance_alerts}",
        content: "High-value invoice ${trigger.payload.Total} rejected.",
      },
    },
    {
      id: "notify_low",
      kind: "action.myapp_record",
      config: {
        form_id: "00000000-0000-0000-0000-000000000002",
        operation: "create",
        data: { Total: "${trigger.payload.Total}", Bucket: "low" },
      },
    },
    {
      id: "stamp_status",
      kind: "logic.sql_write",
      config: {
        connector_key: "00000000-0000-0000-0000-000000000001",
        direction: "source",
        statement: "",
      },
      // Chain the off-flow demo nodes into ONE dagre component so two
      // disconnected singletons don't stack over the main graph (which shifted
      // other nodes out from under their tests).
      next: { success: "settle_wait" },
    },
    {
      id: "settle_wait",
      kind: "logic.delay",
      config: { delay_minutes: 30 },
      next: { success: "fan_out" },
    },
    {
      // `branches` are config (not edges) so referencing existing leaf nodes
      // keeps the off-flow demo chain a single dagre component.
      id: "fan_out",
      kind: "logic.parallel",
      config: { branches: ["notify_high", "notify_rejected"] },
      next: { success: "http_ping" },
    },
    {
      id: "http_ping",
      kind: "action.http_request",
      config: {
        method: "POST",
        url: "https://api.example.com/hooks/${trigger.payload.Id}",
        auth: { type: "bearer", token: "${config.api_token}" },
        body: { total: "${trigger.payload.Total}" },
      },
      next: { success: "for_each_line" },
    },
    {
      id: "for_each_line",
      kind: "logic.foreach",
      config: { items: "${load_history.rows}" },
      next: { success: "classify_ticket" },
    },
    {
      // Showcase the agent.classify (zero-shot classify-and-route) editor —
      // author-supplied categories, no training data.
      id: "classify_ticket",
      kind: "agent.classify",
      config: {
        input: "${trigger.payload.subject} ${trigger.payload.body}",
        categories: ["billing", "technical", "sales", "general"],
        instruction: "Classify the ticket by primary intent.",
      },
      next: { success: "extract_doc" },
    },
    {
      // Showcase the action.document_extract (generic document → structured)
      // editor — parse a PDF/DOCX/image; downstream an Agent reads the markdown.
      id: "extract_doc",
      kind: "action.document_extract",
      config: {
        content: "${load_history.rows[0].AttachmentBytes}",
        filename: "invoice.pdf",
      },
    },
  ],
};

type Mode = "sample" | "paste" | "author" | "kit";

function CanvasDemoPage() {
  const [mode, setMode] = useState<Mode>("sample");

  // ---- read-only modes ---------------------------------------------------
  const [pasted, setPasted] = useState<string>("");
  const [pasteError, setPasteError] = useState<string | null>(null);
  useEffect(() => {
    if (!pasted) setPasted(JSON.stringify(SAMPLE_WORKFLOW, null, 2));
  }, [pasted]);

  const readOnlyWorkflow: DslWorkflow | null = useMemo(() => {
    if (mode === "sample") return SAMPLE_WORKFLOW;
    if (mode === "paste") {
      try {
        const parsed = JSON.parse(pasted) as DslWorkflow;
        if (
          !parsed ||
          !Array.isArray(parsed.triggers) ||
          !Array.isArray(parsed.nodes)
        ) {
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    }
    return null;
  }, [mode, pasted]);

  useEffect(() => {
    if (mode !== "paste") {
      setPasteError(null);
      return;
    }
    try {
      const parsed = JSON.parse(pasted);
      if (
        !parsed ||
        !Array.isArray(parsed.triggers) ||
        !Array.isArray(parsed.nodes)
      ) {
        setPasteError(
          "Doesn't look like a workflow DSL — needs `triggers` and `nodes` arrays.",
        );
      } else {
        setPasteError(null);
      }
    } catch (err) {
      setPasteError(err instanceof Error ? err.message : "Invalid JSON");
    }
  }, [mode, pasted]);

  // ---- author mode -------------------------------------------------------
  // The canvas owns nodes + edges; the page owns the per-node config map
  // (kind + config) so the inspector (3c) can edit fields without
  // re-renders fighting React Flow's internal node store.
  const [authorNodes, setAuthorNodes] = useState<FlowNode[]>(() => {
    const initial = layoutFlowGraph(toFlowGraph(SAMPLE_WORKFLOW));
    return initial.nodes;
  });
  const [authorEdges, setAuthorEdges] = useState<FlowEdge[]>(() => {
    const initial = layoutFlowGraph(toFlowGraph(SAMPLE_WORKFLOW));
    return initial.edges;
  });
  const [authorConfig, setAuthorConfig] = useState<DslConfigMap>(() =>
    configMapFromWorkflow(SAMPLE_WORKFLOW),
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Workflow-level metadata that doesn't live on any single node.
  // ``workflow_id`` stays stable for the lifetime of the canvas
  // session; ``name`` is editable from the toolbar.
  const [workflowName, setWorkflowName] = useState<string>(
    SAMPLE_WORKFLOW.name,
  );
  const [workflowId] = useState<string>("wf_authored_draft");

  // Validation surface (3c). Panel shows on demand; topology issues
  // recompute live; server-side schema check fires on Validate.
  const { token } = useAuth();
  const [validationOpen, setValidationOpen] = useState(false);
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [serverErrors, setServerErrors] = useState<
    Array<{ loc: Array<string | number>; msg: string }>
  >([]);
  const [serverChecking, setServerChecking] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const onGraphChange = useCallback(
    (nodes: FlowNode[], edges: FlowEdge[]) => {
      setAuthorNodes(nodes);
      setAuthorEdges(edges);
    },
    [],
  );

  const onConfigChange = useCallback(
    (nodeId: string, config: Record<string, unknown>) => {
      setAuthorConfig((current) => ({
        ...current,
        [nodeId]: { kind: current[nodeId]?.kind ?? "", config },
      }));
    },
    [],
  );

  const onEdgeLabelChange = useCallback((edgeId: string, label: string) => {
    setAuthorEdges((current) =>
      current.map((edge) => (edge.id === edgeId ? { ...edge, label } : edge)),
    );
  }, []);

  // Demo-only pending connection (D10) — the ghost slot / "+" port wiring
  // the real authoring pages (WorkflowAuthor) own; kept minimal here since
  // this route is a dev sandbox, not a BRIEF acceptance surface.
  const [pendingConnection, setPendingConnection] = useState<PendingConnection | null>(
    null,
  );

  const onNodeCreate = useCallback(
    (kind: string, dropPosition: { x: number; y: number }) => {
      // Generate the id once against the current node set so the
      // canvas + config map agree on which key the new node owns.
      const id = nextNodeId(kind, new Set(authorNodes.map((n) => n.id)));
      const position = pendingConnection
        ? placementForPick(pendingConnection, authorNodes, selectedNodeId)
        : dropPosition;
      const newNode: FlowNode = {
        id,
        type: kindToFlowType(kind),
        position,
        data: {
          label: id,
          kind,
          summary: defaultSummaryFor(kind),
        },
      };
      setAuthorNodes((current) => [...current, newNode]);
      setAuthorConfig((current) => ({
        ...current,
        [id]: { kind, config: {} },
      }));
      if (pendingConnection) {
        setAuthorEdges((current) => [
          ...current,
          pendingConnectionEdge(pendingConnection, id),
        ]);
        setPendingConnection(null);
      }
    },
    [authorNodes, pendingConnection, selectedNodeId],
  );

  const onAddNext = useCallback(
    (nodeId: string, exit: string) => setPendingConnection({ source: nodeId, exit }),
    [],
  );
  const onActivateGhost = useCallback(
    (nodeId: string, exit: string) => setPendingConnection({ source: nodeId, exit }),
    [],
  );

  const liveDsl: DslWorkflow = useMemo(
    () =>
      toDsl(authorNodes, authorEdges, authorConfig, {
        existing: { workflow_id: workflowId, name: workflowName },
      }),
    [authorNodes, authorEdges, authorConfig, workflowId, workflowName],
  );

  const topologyIssues: ValidationIssue[] = useMemo(
    () => (validationOpen ? validateTopology(liveDsl) : []),
    [validationOpen, liveDsl],
  );

  const runServerValidate = useCallback(async () => {
    setServerChecking(true);
    setServerError(null);
    try {
      // Route via the gateway prefix — same pattern the workflowsApi
      // client uses on the subscriptions/topology pages. Direct
      // /api/v2/* calls are not registered on the control-plane
      // public surface.
      const body = await portalFetch<{
        ok: boolean;
        errors: Array<{ loc: Array<string | number>; msg: string }>;
      }>("/api/v1/gateway/workflows/api/v2/workflows/dsl/validate", {
        method: "POST",
        token: token ?? undefined,
        body: { workflow: liveDsl },
      });
      setServerErrors(body.errors ?? []);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Network error");
      setServerErrors([]);
    } finally {
      setServerChecking(false);
    }
  }, [liveDsl, token]);

  const openValidate = useCallback(() => {
    setValidationOpen(true);
    void runServerValidate();
  }, [runServerValidate]);

  const closeValidation = useCallback(() => {
    setValidationOpen(false);
    setServerErrors([]);
    setServerError(null);
  }, []);

  const resetAuthorToSample = useCallback(() => {
    const laid = layoutFlowGraph(toFlowGraph(SAMPLE_WORKFLOW));
    setAuthorNodes(laid.nodes);
    setAuthorEdges(laid.edges);
    setAuthorConfig(configMapFromWorkflow(SAMPLE_WORKFLOW));
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, []);

  const clearAuthor = useCallback(() => {
    setAuthorNodes([]);
    setAuthorEdges([]);
    setAuthorConfig({});
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Workflow canvas · Larasoft</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Workflow canvas</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Visual render + author surface for workflows v2 DSL.
            </p>
          </div>
          <Badge variant="outline">
            {mode === "author"
              ? "3b · author"
              : mode === "kit"
                ? "Lara Canvas kit"
                : "3a · read-only"}
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Mode</CardTitle>
            <CardDescription>
              Pick what the canvas should do.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={mode === "sample" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("sample")}
              >
                Sample workflow
              </Button>
              <Button
                variant={mode === "paste" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("paste")}
              >
                Paste JSON
              </Button>
              <Button
                variant={mode === "author" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("author")}
              >
                Author
              </Button>
              <Button
                variant={mode === "kit" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("kit")}
              >
                Kit
              </Button>
            </div>

            {mode === "paste" && (
              <div className="space-y-2">
                <textarea
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  className="w-full h-48 font-mono text-xs p-2 border rounded-md"
                  spellCheck={false}
                />
                {pasteError && (
                  <Alert variant="destructive">
                    <AlertDescription>{pasteError}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {mode === "kit" && <KitShowcase />}

        {(mode === "sample" || mode === "paste") && (
          <Card>
            <CardHeader>
              <CardTitle>Canvas</CardTitle>
              <CardDescription>
                {readOnlyWorkflow
                  ? `${readOnlyWorkflow.name} · ${readOnlyWorkflow.nodes.length} nodes`
                  : "Nothing to render"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {readOnlyWorkflow ? (
                <div className="border rounded-md overflow-hidden">
                  <ReadOnlyCanvas workflow={readOnlyWorkflow} height={520} />
                </div>
              ) : (
                <Alert>
                  <AlertDescription>
                    Pick a source above to render the canvas.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {mode === "author" && (
          <div className="grid grid-cols-[200px_1fr_340px] gap-3 items-start">
            <Palette
              onPick={(kind) => onNodeCreate(kind, { x: 400, y: 200 })}
              pendingConnection={pendingConnection}
              onClearPending={() => setPendingConnection(null)}
            />

            <div className="space-y-3 min-w-0">
              <Card>
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={workflowName}
                      onChange={(e) => setWorkflowName(e.target.value)}
                      placeholder="Workflow name"
                      className="flex-1 text-base font-medium px-2 py-1 border rounded-md bg-transparent focus:border-primary"
                    />
                    <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                      {workflowId} · v1
                    </span>
                  </div>
                  <div className="flex flex-row items-start justify-between gap-3">
                    <CardDescription className="text-xs flex-1">
                      Drop a palette item to create a node. Drag a handle
                      to connect. Click a node or edge to edit it.
                      Backspace to delete.
                    </CardDescription>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="default" size="sm" onClick={openValidate}>
                        Validate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTestRunOpen((v) => !v)}
                      >
                        Test run
                      </Button>
                      <Button variant="outline" size="sm" onClick={resetAuthorToSample}>
                        Reset to sample
                      </Button>
                      <Button variant="outline" size="sm" onClick={clearAuthor}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-md overflow-hidden">
                    <EditableCanvas
                      nodes={authorNodes}
                      edges={authorEdges}
                      configMap={authorConfig}
                      onGraphChange={onGraphChange}
                      onNodeCreate={onNodeCreate}
                      selectedNodeId={selectedNodeId}
                      onNodeSelect={setSelectedNodeId}
                      selectedEdgeId={selectedEdgeId}
                      onEdgeSelect={setSelectedEdgeId}
                      // Bumped from 520 for the kit rebuild (D7: LR instead
                      // of TB) — SAMPLE_WORKFLOW's 15 nodes read much WIDER
                      // now, and at 520px tall the fitView zoom-out needed
                      // to fit them left the fixed-size minimap (bottom
                      // right) covering a large enough fraction of the pane
                      // that it intercepted clicks on nodes under it.
                      height={900}
                      // The sample's longest LR chain is ~1700px wide and this
                      // sandbox's canvas column is ~420px at a 1280px viewport,
                      // so React Flow's default minZoom (0.5) left fitView
                      // clipping both ends of the graph — the leftmost cards
                      // sat over the palette column, where a click landed on
                      // a palette item instead (ws3-builders / ws4-control).
                      // 0.15 fits the whole sample; the real authoring pages
                      // keep the default.
                      minZoom={0.15}
                      onAddNext={onAddNext}
                      onActivateGhost={onActivateGhost}
                    />
                  </div>
                </CardContent>
              </Card>

              {validationOpen && (
                <ValidationPanel
                  issues={topologyIssues}
                  serverErrors={serverErrors}
                  serverChecking={serverChecking}
                  serverError={serverError}
                  onRevalidate={runServerValidate}
                  onClose={closeValidation}
                  onSelectNode={(id) => {
                    setSelectedNodeId(id);
                    setSelectedEdgeId(null);
                  }}
                />
              )}

              {testRunOpen && (
                <TestRunPanel
                  dsl={liveDsl}
                  token={token ?? undefined}
                  onClose={() => setTestRunOpen(false)}
                  onSelectNode={(id) => {
                    setSelectedNodeId(id);
                    setSelectedEdgeId(null);
                  }}
                />
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Live DSL</CardTitle>
                  <CardDescription>
                    What the canvas would save right now (
                    {liveDsl.nodes.length} node
                    {liveDsl.nodes.length === 1 ? "" : "s"} ·{" "}
                    {liveDsl.triggers.length} trigger
                    {liveDsl.triggers.length === 1 ? "" : "s"}).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="text-[11px] font-mono bg-muted/50 p-3 rounded-md overflow-auto max-h-72">
                    {JSON.stringify(liveDsl, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>

            <Inspector
              selectedNodeId={selectedNodeId}
              selectedEdgeId={selectedEdgeId}
              nodes={authorNodes}
              edges={authorEdges}
              configMap={authorConfig}
              onConfigChange={onConfigChange}
              onEdgeLabelChange={onEdgeLabelChange}
            />
          </div>
        )}
      </div>
    </>
  );
}
